import { formatDate } from '@/lib/date-time';
/**
 * @file components/audit-tasks/field/FindingsPanel.tsx
 * @description NC 紀錄管理內聯面板（Table + 三模式 Drawer）。
 *
 * 從 routes/_app/audit/records/$caseId.findings.tsx 搬遷邏輯，去 PageShell。
 * 三個模式共用一個 Drawer：mode = 'create' | 'edit' | 'view'。
 *
 * Schema（M2 / A.7）：
 * - severity 中文 enum：'重大' | '一般' | '觀察事項' | '改善建議'
 * - 建立（POST createAuditFindingSchema）：severity / clauseRef? / description /
 *   correctionRequired (boolean, default true) / correctionDueDate? (ISO datetime)
 * - 更新（PATCH updateAuditFindingSchema, A.7 加的）：所有欄位 optional，
 *   nullable 欄位（clauseRef / correctionDueDate / clientResponse）以 null 顯式清除
 * - shared 用 boolean，service 端轉成 0/1 寫入 DB
 *
 * 新增 NC 支援離線 native outbox；更新 NC 走 patchWithOutbox。
 */

import { useState } from 'react';
import { message } from '@/lib/antd-app';
import {
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Edit3, Eye, Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { postWithOutbox, patchWithOutbox } from '@/lib/api-with-outbox';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';
import { pushOutboxReliable } from '@/pwa/db';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';

// ─── 類型與常數 ───────────────────────────────────────────────────────────────

const SEVERITIES = ['重大', '一般', '觀察事項', '改善建議'] as const;
type Severity = (typeof SEVERITIES)[number];

/** NC / 發現事項（GET 回傳形狀的最小子集，多餘欄位忽略） */
interface Finding {
  id: number;
  code: string;
  caseId: number;
  severity: Severity;
  clauseRef: string | null;
  description: string;
  /** DB 存 0/1，前端顯示時轉布林 */
  correctionRequired: number;
  correctionDueDate: string | null;
  clientResponse: string | null;
  signatureFileId: number | null;
  closedAt: string | null;
  createdAt: string;
}

/** Drawer 三種模式：新增、編輯、檢視（唯讀） */
type Mode = 'create' | 'edit' | 'view';

/** Drawer 表單值（dayjs 物件 + 字串 + 布林） */
interface FormValues {
  severity: Severity;
  clauseRef?: string | null;
  description: string;
  correctionRequired?: boolean;
  correctionDueDate?: Dayjs | null;
  clientResponse?: string | null;
}

interface Props {
  /** 對應稽核案件 ID */
  caseId: number;
}

/** 嚴重程度 → antd Tag 顏色 */
const SEVERITY_COLOR: Record<Severity, string> = {
  重大: 'error',
  一般: 'warning',
  觀察事項: 'processing',
  改善建議: 'default',
};

// ─── 元件 ────────────────────────────────────────────────────────────────────

/**
 * NC 紀錄管理內聯面板。
 *
 * 使用情境：被 PhaseField 嵌入到稽核任務頁，不再走獨立路由。
 */
export function FindingsPanel({ caseId }: Props) {
  const qc = useQueryClient();
  // 序61：管理組（manager）唯讀——無開立 NC 能力時隱藏「新增/編輯」，只保留「檢視」
  const { canFinding } = useFieldWriteAccess();

  // 篩選條件（純前端 filter）
  const [filterSeverity, setFilterSeverity] = useState<Severity | undefined>(undefined);

  // Drawer 狀態
  const [mode, setMode] = useState<Mode | null>(null);
  const [editing, setEditing] = useState<Finding | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm<FormValues>();

  // ─── 列表查詢 ───────────────────────────────────────────────────────────────
  const findingsQ = useQuery<{ items: Finding[]; total: number }>({
    queryKey: ['findings', caseId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ items: Finding[]; total: number }>(
        `/api/cases/${caseId}/findings`,
        { scope: 'field-data' },
      );
      return res.data;
    },
  });

  const filtered = (findingsQ.data?.items ?? []).filter((f) => {
    if (filterSeverity && f.severity !== filterSeverity) return false;
    return true;
  });

  // ─── 新增 NC ────────────────────────────────────────────────────────────────
  const createFinding = async (values: FormValues) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const body = {
        severity: values.severity,
        clauseRef: values.clauseRef || undefined,
        description: values.description,
        correctionRequired: values.correctionRequired ?? true,
        correctionDueDate: values.correctionDueDate?.toISOString(),
      };
      const url = `/api/cases/${caseId}/findings`;
      const outboxMeta = {
        entityType: 'audit-finding',
        caseId,
      };
      const result = !navigator.onLine
        ? await pushOutboxReliable({ method: 'POST', url, body, ...outboxMeta }).then(() => ({
            offline: true,
          }))
        : await postWithOutbox(url, body, outboxMeta);

      if ((result as { offline?: boolean })?.offline) {
        message.success('NC 已加入離線佇列，連線後自動同步');
      } else {
        message.success('NC 已開立');
      }
      closeDrawer();
      void qc.invalidateQueries({ queryKey: ['findings', caseId] });
    } catch (err) {
      console.error('[findings-panel] create finding failed', err);
      message.error(err instanceof Error ? err.message : 'NC 提交失敗');
    } finally {
      setIsCreating(false);
    }
  };

  // ─── Mutation：編輯 NC ──────────────────────────────────────────────────────
  // 改走 patchWithOutbox：線上直發 API，離線時推 PWA outbox 排隊。
  const update = useMutation({
    mutationFn: ({ id, values }: { id: number; values: FormValues }) => {
      // PATCH 允許 null 清除欄位；undefined 表示不變。
      const body = {
        severity: values.severity,
        clauseRef: values.clauseRef ?? null,
        description: values.description,
        correctionRequired: values.correctionRequired,
        correctionDueDate: values.correctionDueDate ? values.correctionDueDate.toISOString() : null,
        clientResponse: values.clientResponse ?? null,
      };
      return patchWithOutbox(`/api/cases/${caseId}/findings/${id}`, body, {
        entityType: 'audit-finding',
        entityClientLocalId: String(id),
      });
    },
    onSuccess: (result) => {
      if ((result as { offline?: boolean })?.offline) {
        message.success('NC 更新已加入離線佇列');
      } else {
        message.success('NC 已更新');
      }
      closeDrawer();
      void qc.invalidateQueries({ queryKey: ['findings', caseId] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // ─── Drawer 開關 ────────────────────────────────────────────────────────────
  const closeDrawer = () => {
    setMode(null);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = () => {
    setMode('create');
    setEditing(null);
    form.resetFields();
    // 新增時使用常用現場預設值。
    form.setFieldsValue({ severity: '一般', correctionRequired: true });
  };

  const openEdit = (f: Finding) => {
    setMode('edit');
    setEditing(f);
    form.setFieldsValue({
      severity: f.severity,
      clauseRef: f.clauseRef,
      description: f.description,
      correctionRequired: f.correctionRequired === 1,
      correctionDueDate: f.correctionDueDate ? dayjs(f.correctionDueDate) : null,
      clientResponse: f.clientResponse,
    });
  };

  const openView = (f: Finding) => {
    setMode('view');
    setEditing(f);
    form.setFieldsValue({
      severity: f.severity,
      clauseRef: f.clauseRef,
      description: f.description,
      correctionRequired: f.correctionRequired === 1,
      correctionDueDate: f.correctionDueDate ? dayjs(f.correctionDueDate) : null,
      clientResponse: f.clientResponse,
    });
  };

  /** Form 提交分流到 create / update */
  const submit = (values: FormValues) => {
    if (mode === 'create') {
      void createFinding(values);
    } else if (mode === 'edit' && editing) {
      update.mutate({ id: editing.id, values });
    }
  };

  // ─── 表格欄位 ───────────────────────────────────────────────────────────────
  const cols: ColumnsType<Finding> = [
    {
      title: '編號',
      dataIndex: 'code',
      width: 140,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '嚴重程度',
      dataIndex: 'severity',
      width: 110,
      render: (s: Severity) => <Tag color={SEVERITY_COLOR[s]}>{s}</Tag>,
    },
    {
      title: '條款',
      dataIndex: 'clauseRef',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '改善必要',
      dataIndex: 'correctionRequired',
      width: 90,
      render: (v: number) => (v === 1 ? '需' : '否'),
    },
    {
      title: '截止日',
      dataIndex: 'correctionDueDate',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '操作',
      width: canFinding ? 140 : 90,
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" icon={<Eye size={12} />} onClick={() => openView(r)}>
            檢視
          </Button>
          {/* 序61：無開立 NC 能力（如管理組）僅可檢視，不顯示編輯 */}
          {canFinding && (
            <Button type="link" size="small" icon={<Edit3 size={12} />} onClick={() => openEdit(r)}>
              編輯
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // ─── Drawer 標題與唯讀判斷 ───────────────────────────────────────────────────
  const drawerTitle =
    mode === 'create'
      ? '新增 NC 紀錄'
      : mode === 'edit'
        ? `編輯 NC ${editing?.code ?? ''}`
        : `NC 詳情 ${editing?.code ?? ''}`;
  const isReadonly = mode === 'view';

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 篩選列 + 新增按鈕 */}
        <Space wrap>
          <Select
            allowClear
            placeholder="嚴重程度"
            style={{ width: 160 }}
            value={filterSeverity}
            onChange={setFilterSeverity}
            options={SEVERITIES.map((s) => ({ value: s, label: s }))}
          />
          {/* 序61：無開立 NC 能力（如管理組）不顯示「新增 NC」 */}
          {canFinding && (
            <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
              新增 NC
            </Button>
          )}
        </Space>

        {/* 列表 */}
        {findingsQ.isLoading ? (
          <Typography.Text>載入中…</Typography.Text>
        ) : filtered.length ? (
          <Table<Finding>
            rowKey="id"
            dataSource={filtered}
            columns={cols}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
          />
        ) : (
          <Empty description="尚無 NC 紀錄" />
        )}
      </Space>

      {/* 三模式共用 Drawer */}
      <Drawer
        title={drawerTitle}
        open={mode != null}
        onClose={closeDrawer}
        width={520}
        footer={
          isReadonly ? (
            <Button onClick={closeDrawer}>關閉</Button>
          ) : (
            <Space>
              <Button onClick={closeDrawer}>取消</Button>
              <Button
                type="primary"
                loading={isCreating || update.isPending}
                onClick={() => form.submit()}
              >
                {mode === 'create' ? '提交' : '儲存'}
              </Button>
            </Space>
          )
        }
      >
        <Form form={form} layout="vertical" onFinish={submit} disabled={isReadonly}>
          <Form.Item name="severity" label="嚴重程度" rules={[{ required: true }]}>
            <Select options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="clauseRef" label="標準條款">
            <Input maxLength={100} placeholder="例如：4.2.3" />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '請填寫描述' }]}
          >
            <Input.TextArea rows={4} maxLength={5000} showCount />
          </Form.Item>
          <Form.Item
            name="correctionRequired"
            label="是否需要改正"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="需" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item name="correctionDueDate" label="改正截止日">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {mode !== 'create' && (
            <Form.Item name="clientResponse" label="客戶回應">
              <Input.TextArea rows={3} maxLength={5000} showCount />
            </Form.Item>
          )}
        </Form>
      </Drawer>
    </>
  );
}
